import React, { useRef, useEffect } from 'react';

interface SignaturePadProps {
    width?: number;
    height?: number;
    backgroundColor?: string;
    penColor?: string;
    onChange?: (dataUrl: string | null) => void;
}

const SignaturePad: React.FC<SignaturePadProps> = ({
    width = 500,
    height = 180,
    backgroundColor = '#fff',
    penColor = '#111',
    onChange
}) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const isDrawingRef = useRef(false);
    const hasStrokeRef = useRef(false);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // Increase line width and smoothness
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 3; // Increased from 2 to 3
        ctx.strokeStyle = penColor;
        ctx.shadowBlur = 1; // Add slight blur for smoother lines
        ctx.shadowColor = penColor;
    }, [backgroundColor, penColor]);

    const getPos = (e: React.MouseEvent | React.TouchEvent) => {
        const canvas = canvasRef.current!;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        
        if ('touches' in e) {
            const t = e.touches[0];
            return {
                x: (t.clientX - rect.left) * scaleX,
                y: (t.clientY - rect.top) * scaleY
            };
        }
        const m = e as React.MouseEvent;
        return {
            x: (m.clientX - rect.left) * scaleX,
            y: (m.clientY - rect.top) * scaleY
        };
    };

    const start = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        const ctx = canvasRef.current!.getContext('2d');
        if (!ctx) return;
        const { x, y } = getPos(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
        isDrawingRef.current = true;
    };

    const move = (e: React.MouseEvent | React.TouchEvent) => {
        if (!isDrawingRef.current) return;
        e.preventDefault();
        const ctx = canvasRef.current!.getContext('2d');
        if (!ctx) return;
        const { x, y } = getPos(e);
        
        // Use quadratic curves for smoother lines
        if (hasStrokeRef.current) {
            const lastPoint = ctx.getTransform().transformPoint(new DOMPoint(x, y));
            const xc = (lastPoint.x + x) / 2;
            const yc = (lastPoint.y + y) / 2;
            ctx.quadraticCurveTo(lastPoint.x, lastPoint.y, xc, yc);
        } else {
            ctx.lineTo(x, y);
        }
        
        ctx.stroke();
        hasStrokeRef.current = true;
        if (onChange) onChange(canvasRef.current!.toDataURL('image/png'));
    };

    const end = (e?: React.MouseEvent | React.TouchEvent) => {
        if (e) e.preventDefault();
        isDrawingRef.current = false;
    };

    const clear = () => {
        const canvas = canvasRef.current!;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        hasStrokeRef.current = false;
        if (onChange) onChange(null);
    };

    return (
        <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 8 }}>
            <canvas
                ref={canvasRef}
                width={width}
                height={height}
                style={{ border: '1px solid #d1d5db', borderRadius: 8, touchAction: 'none', background: backgroundColor }}
                onMouseDown={start}
                onMouseMove={move}
                onMouseUp={end}
                onMouseLeave={end}
                onTouchStart={start}
                onTouchMove={move}
                onTouchEnd={end}
            />
            <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={clear} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', background: '#fff', borderRadius: 6, cursor: 'pointer' }}>Clear</button>
            </div>
        </div>
    );
};

export default SignaturePad;


